// DiaryToolbar — formatting toolbar for Tiptap editor.
// Apple HIG pill style using lucide-react icons.
// Active state synced with editor commands.

import { type Editor } from "@tiptap/react";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code,
  Heading1,
  Heading2,
  Heading3,
} from "lucide-react";

import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";

interface DiaryToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, label, children }: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "h-8 w-8 p-0 rounded-md",
        active ? "bg-you/20 text-you-strong" : "text-ink-2 hover:text-ink-1 hover:bg-black/5",
      )}
    >
      {children}
    </Button>
  );
}

export function DiaryToolbar({ editor, disabled }: DiaryToolbarProps) {
  return (
    <div
      className="sticky top-0 z-[1] flex items-center gap-0.5 px-2 py-1.5 border-b border-hairline bg-paper overflow-x-auto"
      role="toolbar"
      aria-label="格式化工具"
    >
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        disabled={disabled}
        label="粗體"
      >
        <Bold className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        disabled={disabled}
        label="斜體"
      >
        <Italic className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
        disabled={disabled}
        label="刪除線"
      >
        <Strikethrough className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-hairline mx-1" aria-hidden />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive("heading", { level: 1 })}
        disabled={disabled}
        label="標題 1"
      >
        <Heading1 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive("heading", { level: 2 })}
        disabled={disabled}
        label="標題 2"
      >
        <Heading2 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive("heading", { level: 3 })}
        disabled={disabled}
        label="標題 3"
      >
        <Heading3 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-hairline mx-1" aria-hidden />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        disabled={disabled}
        label="無序列表"
      >
        <List className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        disabled={disabled}
        label="有序列表"
      >
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-hairline mx-1" aria-hidden />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive("blockquote")}
        disabled={disabled}
        label="引用"
      >
        <Quote className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive("code")}
        disabled={disabled}
        label="行內代碼"
      >
        <Code className="w-3.5 h-3.5" />
      </ToolbarButton>
    </div>
  );
}
